/* Filtering, sorting and "load more" via the Section Rendering API.
   Works for collections and search results. Keeps desktop + mobile filter
   forms in sync, updates the URL, shows a skeleton while loading. */
(() => {
  const ik = (window.ikTheme = window.ikTheme || {});

  class IkFacets extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.baseUrl = this.dataset.baseUrl || window.location.pathname;
      this.controller = null;

      this.addEventListener('change', (event) => {
        const target = event.target;
        if (target.matches('[data-facets-sort]')) {
          this.syncSorts(target.value);
          this.update(this.buildParams(target.value));
        } else if (target.closest('[data-facets-form]') && !target.matches('[data-facets-price]')) {
          this.update(this.buildParams(null, target.closest('[data-facets-form]')));
        }
      });

      const debouncedPrice = ik.debounce((form) => this.update(this.buildParams(null, form)), 600);
      this.addEventListener('input', (event) => {
        if (event.target.matches('[data-facets-price]')) debouncedPrice(event.target.closest('[data-facets-form]'));
      });

      this.addEventListener('submit', (event) => {
        if (event.target.matches('[data-facets-form]')) {
          event.preventDefault();
          this.update(this.buildParams(null, event.target));
        }
      });

      this.addEventListener('click', (event) => {
        const link = event.target.closest('[data-facets-link]');
        if (!link) return;
        event.preventDefault();
        const url = new URL(link.href, window.location.origin);
        this.render(url.search.replace(/^\?/, ''), true);
      });

      window.addEventListener('popstate', () => {
        this.render(window.location.search.replace(/^\?/, ''), false);
      });
    }

    get forms() {
      return Array.from(this.querySelectorAll('[data-facets-form]'));
    }

    syncSorts(value) {
      this.querySelectorAll('[data-facets-sort]').forEach((select) => {
        if (select.value !== value) select.value = value;
        const label = select.closest('.results-bar__sort')?.querySelector('[data-sort-label]');
        if (label) label.textContent = select.options[select.selectedIndex]?.text || '';
      });
      this.querySelectorAll('[data-facets-sort-hidden]').forEach((input) => {
        input.value = value;
      });
    }

    buildParams(sortValue, form) {
      const source = form || this.forms[0];
      const params = new URLSearchParams();
      if (source) {
        new FormData(source).forEach((value, key) => {
          if (key === 'sort_by') return;
          if (value === '') return;
          params.append(key, value);
        });
      }
      const currentSort = sortValue || this.querySelector('[data-facets-sort]')?.value || source?.querySelector('[data-facets-sort-hidden]')?.value;
      if (currentSort) params.set('sort_by', currentSort);
      params.delete('page');
      return params.toString();
    }

    update(query) {
      this.render(query, true);
    }

    setLoading(on) {
      this.classList.toggle('is-loading', on);
      this.querySelectorAll('[data-results]').forEach((el) => el.setAttribute('aria-busy', on ? 'true' : 'false'));
    }

    rememberOpenFacets() {
      const open = new Set();
      this.querySelectorAll('[data-facet][open]').forEach((el) => open.add(el.dataset.facet));
      return open;
    }

    async render(query, pushState) {
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      this.setLoading(true);
      const url = `${this.baseUrl}?${query}${query ? '&' : ''}section_id=${this.sectionId}`;
      try {
        const response = await fetch(url, { signal: this.controller.signal });
        if (!response.ok) throw new Error(response.status);
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        this.apply(doc);
        if (pushState) window.history.pushState({}, '', `${this.baseUrl}${query ? `?${query}` : ''}`);
        this.dispatchEvent(new CustomEvent('ik:facets:updated', { bubbles: true }));
      } catch (error) {
        if (error.name === 'AbortError') return;
        window.location.href = `${this.baseUrl}${query ? `?${query}` : ''}`;
      } finally {
        this.setLoading(false);
      }
    }

    apply(doc) {
      const openFacets = this.rememberOpenFacets();
      const focused = document.activeElement;
      const focusId = focused && this.contains(focused) ? focused.id : null;

      // Results + pagination
      doc.querySelectorAll('[data-results]').forEach((fresh, index) => {
        const current = this.querySelectorAll('[data-results]')[index];
        if (current) current.innerHTML = fresh.innerHTML;
      });
      // Counts, active chips, apply-button label
      ['[data-results-count]', '[data-results-count-short]', '[data-facets-active]', '[data-facets-apply]', '[data-results-shown]', '[data-filter-count]'].forEach((selector) => {
        const freshEls = doc.querySelectorAll(selector);
        this.querySelectorAll(selector).forEach((current, index) => {
          const fresh = freshEls[index];
          if (!fresh) return;
          current.innerHTML = fresh.innerHTML;
          Array.from(fresh.attributes).forEach((attr) => {
            if (attr.name.startsWith('data-') || attr.name === 'hidden') current.setAttribute(attr.name, attr.value);
          });
          if (!fresh.hasAttribute('hidden')) current.removeAttribute('hidden');
        });
      });
      // Filter forms (desktop + mobile) by id
      doc.querySelectorAll('[data-facets-form]').forEach((freshForm) => {
        const current = this.querySelector(`[data-facets-form][data-facets-id="${freshForm.dataset.facetsId}"]`);
        if (!current) return;
        current.innerHTML = freshForm.innerHTML;
        current.querySelectorAll('[data-facet]').forEach((details) => {
          if (openFacets.size) details.open = openFacets.has(details.dataset.facet);
        });
      });
      // Sort selects
      const freshSort = doc.querySelector('[data-facets-sort]');
      if (freshSort) this.syncSorts(freshSort.value);
      // Empty state wrappers
      ['[data-results-empty]'].forEach((selector) => {
        const fresh = doc.querySelector(selector);
        const current = this.querySelector(selector);
        if (fresh && current) {
          current.innerHTML = fresh.innerHTML;
          current.hidden = fresh.hidden;
        }
      });

      if (focusId) {
        const el = document.getElementById(focusId);
        if (el) el.focus({ preventScroll: true });
      }
    }
  }

  class IkLoadMore extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button, a');
      if (!this.button) return;
      this.button.addEventListener('click', (event) => {
        event.preventDefault();
        this.load();
      });
    }

    async load() {
      const url = this.dataset.url;
      const sectionId = this.dataset.sectionId;
      if (!url || !sectionId) return;
      this.button.setAttribute('aria-busy', 'true');
      try {
        const joiner = url.includes('?') ? '&' : '?';
        const response = await fetch(`${url}${joiner}section_id=${sectionId}`);
        if (!response.ok) throw new Error(response.status);
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const freshGrid = doc.querySelector('[data-results-grid]');
        const grid = this.closest('[data-results]')?.querySelector('[data-results-grid]') || document.querySelector('[data-results-grid]');
        if (freshGrid && grid) {
          const fragment = document.createDocumentFragment();
          Array.from(freshGrid.children).forEach((child) => fragment.appendChild(child));
          grid.appendChild(fragment);
        }
        const freshMore = doc.querySelector('[data-load-more]');
        const shown = document.querySelector('[data-results-shown]');
        const freshShown = doc.querySelector('[data-results-shown]');
        if (shown && freshShown) shown.innerHTML = freshShown.innerHTML;
        if (freshMore && freshMore.dataset.url) {
          this.dataset.url = freshMore.dataset.url;
          window.history.replaceState({}, '', url);
        } else {
          this.remove();
        }
      } catch (error) {
        window.location.href = url;
      } finally {
        if (this.button) this.button.removeAttribute('aria-busy');
      }
    }
  }

  if (!customElements.get('ik-facets')) customElements.define('ik-facets', IkFacets);
  if (!customElements.get('ik-load-more')) customElements.define('ik-load-more', IkLoadMore);
})();
